import { attributes } from '../../core/ids-attributes';
import { IdsConstructor } from '../../core/ids-element';
import { EventsMixinInterface } from '../ids-events-mixin/ids-events-mixin';
import { IdsInputInterface } from '../../components/ids-input/ids-input-attributes';
import { LocaleMixinInterface } from '../ids-locale-mixin/ids-locale-mixin';

export interface FieldIndicatorHandler {
  onFieldIndicatorChange?(enabled: boolean): void;
}

type Constraints = IdsConstructor<EventsMixinInterface & FieldIndicatorHandler & LocaleMixinInterface>;

/** Valid field indicator status values */
const FIELD_INDICATOR_TYPES = ['neutral', 'info', 'success', 'caution', 'warning', 'error'] as const;
export type FieldIndicatorType = typeof FIELD_INDICATOR_TYPES[number];

/** Maps indicator type to its corresponding icon name */
const INDICATOR_ICON_MAP: Record<FieldIndicatorType, string> = {
  neutral: 'empty-circle',
  info: 'info',
  success: 'success',
  caution: 'exclamation',
  warning: 'alert',
  error: 'error',
};

/**
 * Adds a persistent colored triangle indicator and matching border to form fields.
 * Used for custom field states like "TBD", "auto-generated", or any app-defined meaning.
 * @param {any} superclass Accepts a superclass and creates a new subclass from it
 * @returns {any} The extended object
 */
const IdsFieldIndicatorMixin = <T extends Constraints>(superclass: T) => class extends superclass {
  constructor(...args: any[]) {
    super(...args);
  }

  connectedCallback() {
    super.connectedCallback?.();
    if (this.fieldIndicator) {
      this.#renderIndicator();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#detachFocusEvents();
    this.fieldIndicatorContainer = null;
  }

  static get attributes() {
    return [
      ...(superclass as any).attributes,
      attributes.FIELD_INDICATOR,
      attributes.FIELD_INDICATOR_MESSAGE,
    ];
  }

  /** Container element holding the indicator icon */
  fieldIndicatorContainer?: HTMLElement | SVGElement | null;

  /**
   * Set the field indicator type.
   * Accepts: 'neutral', 'info', 'success', 'caution', 'warning', 'error', or null to remove.
   * @param {FieldIndicatorType | string | null} value The indicator type
   */
  set fieldIndicator(value: FieldIndicatorType | string | null) {
    if (value && FIELD_INDICATOR_TYPES.includes(value as FieldIndicatorType)) {
      this.setAttribute(attributes.FIELD_INDICATOR, value);
    } else {
      this.removeAttribute(attributes.FIELD_INDICATOR);
    }
    this.#handleFieldIndicator();
    if (typeof this.onFieldIndicatorChange === 'function') {
      this.onFieldIndicatorChange(!!value);
    }
  }

  get fieldIndicator(): FieldIndicatorType | null {
    const val = this.getAttribute(attributes.FIELD_INDICATOR);
    if (val && FIELD_INDICATOR_TYPES.includes(val as FieldIndicatorType)) {
      return val as FieldIndicatorType;
    }
    return null;
  }

  /**
   * Set the tooltip message shown when hovering the info icon
   * @param {string | null} value The message text
   */
  set fieldIndicatorMessage(value: string | null) {
    if (value) {
      this.setAttribute(attributes.FIELD_INDICATOR_MESSAGE, value);
    } else {
      this.removeAttribute(attributes.FIELD_INDICATOR_MESSAGE);
    }
    this.#updateInfoIcon();
  }

  get fieldIndicatorMessage(): string | null {
    return this.getAttribute(attributes.FIELD_INDICATOR_MESSAGE);
  }

  /**
   * Attach language change listener to update RTL and re-translate error messages
   */
  #attachLanguageListener() {
    this.offEvent('languagechange.errortracker');
    this.onEvent('languagechange.errortracker', this, () => {
      this.#onLanguageChange();
    });
  }

  #detachLanguageListener() {
    this.offEvent('languagechange.errortracker');
  }

  /**
   * Handle showing or removing the indicator based on attribute state
   */
  #handleFieldIndicator() {
    if (this.fieldIndicator) {
      this.#renderIndicator();
      this.#attachLanguageListener();
    } else {
      this.#removeIndicator();
      this.#detachLanguageListener();
    }
  }

  /**
   * Render the indicator icon into the field container
   */
  #renderIndicator() {
    const thisAsInput = this as IdsInputInterface;
    const thisAsAny = this as any;

    // Resolve the field container across different component types
    if (this.tagName === 'IDS-DROPDOWN' || this.tagName === 'IDS-MULTISELECT') {
      this.fieldIndicatorContainer = thisAsInput.input?.fieldContainer;
    } else if (thisAsAny.triggerField?.fieldContainer) {
      // Compound components like ids-date-picker and ids-time-picker
      this.fieldIndicatorContainer = thisAsAny.triggerField.fieldContainer;
    } else {
      this.fieldIndicatorContainer = thisAsInput.fieldContainer;
    }

    let icon = this.fieldIndicatorContainer?.querySelector('.icon-field-indicator');
    if (!icon) {
      icon = document.createElement('ids-icon');
      icon.setAttribute('icon', 'dirty-filled');
      icon.setAttribute('size', 'small');
      icon.setAttribute('width', '12');
      icon.setAttribute('height', '12');
      icon.setAttribute('part', 'field-indicator-icon');
      icon.className = 'icon-field-indicator';
      (icon as HTMLElement).style.pointerEvents = 'none';

      if (this.localeAPI?.isRTL()) {
        icon.setAttribute('dir', 'rtl');
      }

      this.fieldIndicatorContainer?.prepend(icon);
    }

    this.#applyColor();
    this.#updateInfoIcon();
    this.#attachFocusEvents();
  }

  /**
   * Remove the indicator icon, info icon, and reset classes
   */
  #removeIndicator() {
    this.#detachFocusEvents();
    this.fieldIndicatorContainer?.classList.remove('has-focus');
    const icon = this.fieldIndicatorContainer?.querySelector('.icon-field-indicator');
    if (icon) {
      icon.remove();
    }
    this.#removeInfoIcon();
    this.#removeIndicatorClass();
  }

  /**
   * Render or update the info icon with tooltip inside the field container
   */
  #updateInfoIcon() {
    if (!this.fieldIndicator || !this.fieldIndicatorMessage) {
      this.#removeInfoIcon();
      return;
    }

    let infoIcon = this.fieldIndicatorContainer?.querySelector('.icon-field-indicator-info') as HTMLElement;
    const isRtl = this.localeAPI.isRTL();

    if (!infoIcon) {
      infoIcon = document.createElement('ids-icon');
      infoIcon.setAttribute('size', 'medium');
      infoIcon.setAttribute('part', 'field-indicator-info-icon');
      infoIcon.className = 'icon-field-indicator-info';

      const triggerBtn = (this.fieldIndicatorContainer?.querySelector('slot[name="trigger-end"]') as HTMLSlotElement);

      if (triggerBtn && triggerBtn?.assignedElements()) {
        this.fieldIndicatorContainer?.insertBefore(infoIcon, triggerBtn);
      } else {
        this.fieldIndicatorContainer?.appendChild(infoIcon);
      }
    }

    // Update icon to match the indicator type
    const iconName = INDICATOR_ICON_MAP[this.fieldIndicator] || 'info';
    infoIcon.setAttribute('icon', iconName);

    // Create or update the ids-tooltip element
    let tooltipEl = this.fieldIndicatorContainer?.querySelector('.field-indicator-tooltip') as any;
    if (!tooltipEl) {
      tooltipEl = document.createElement('ids-tooltip');
      tooltipEl.className = 'field-indicator-tooltip';
      tooltipEl.placement = isRtl ? 'left' : 'right';
      this.fieldIndicatorContainer?.appendChild(tooltipEl);

      // Set target directly as element reference (not selector) since both are in shadow DOM
      tooltipEl.target = infoIcon;
      tooltipEl.placement = this.localeAPI?.isRTL() ? 'left' : 'right';
    }

    tooltipEl.textContent = this.fieldIndicatorMessage;
  }

  /**
   * Remove the info icon and tooltip from the field container
   */
  #removeInfoIcon() {
    this.fieldIndicatorContainer?.querySelector('.icon-field-indicator-info')?.remove();
    this.fieldIndicatorContainer?.querySelector('.field-indicator-tooltip')?.remove();
  }

  /**
   * Apply the indicator type class to the field container
   */
  #applyColor() {
    const type = this.fieldIndicator;
    if (!type) return;

    this.#removeIndicatorClass();
    this.fieldIndicatorContainer?.classList.add(`field-indicator-${type}`);
  }

  /**
   * Remove all field-indicator-* classes from the container
   */
  #removeIndicatorClass() {
    FIELD_INDICATOR_TYPES.forEach((t) => {
      this.fieldIndicatorContainer?.classList.remove(`field-indicator-${t}`);
    });
  }

  /**
   * Attach focus/blur events to toggle has-focus class on the field container
   */
  #attachFocusEvents() {
    this.offEvent('focusin.field-indicator');
    this.offEvent('focusout.field-indicator');

    this.onEvent('focusin.field-indicator', this, () => {
      if (this.fieldIndicator) {
        this.fieldIndicatorContainer?.classList.add('has-focus');
      }
    });

    this.onEvent('focusout.field-indicator', this, () => {
      this.fieldIndicatorContainer?.classList.remove('has-focus');
    });
  }

  /**
   * Remove focus/blur event listeners
   */
  #detachFocusEvents() {
    this.offEvent('focusin.field-indicator');
    this.offEvent('focusout.field-indicator');
  }

  /** Handle language changes for RTL support and re-translate error messages */
  #onLanguageChange() {
    const icon = this.fieldIndicatorContainer?.querySelector('.icon-field-indicator');
    if (this.localeAPI?.isRTL()) {
      icon?.setAttribute('dir', 'rtl');
    } else {
      icon?.removeAttribute('dir');
    }

    // Update tooltip placement for RTL
    const tooltipEl = this.fieldIndicatorContainer?.querySelector('.field-indicator-tooltip') as any;
    if (tooltipEl) {
      tooltipEl.placement = this.localeAPI?.isRTL() ? 'left' : 'right';
    }
  }
};

export default IdsFieldIndicatorMixin;
